import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  DialogActions,
} from "@mui/material";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import ApplicationSearchBar from "./ApplicationSearchBar";
import { styled } from "@mui/material/styles";

const apiUrl = process.env.REACT_APP_API_URL;

// Reused helper functions from DynamicFormTable
const cleanString = (str) => {
  if (!str) return "";
  return str.replace(/^\[|\]$|'|"/g, "").trim();
};

const parseStringToArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch (e) {
      return data
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
    }
  }
  return [];
};

// TruncatedContent component (updated with cleanString)
const TruncatedContent = ({
  content,
  maxLength = 20,
  openDialog,
  asChip = false,
  sx,
}) => {
  const cleanedContent = cleanString(content);
  if (!cleanedContent) return "-";

  const needsTruncation = cleanedContent.length > maxLength;
  const truncatedText = needsTruncation
    ? cleanedContent.slice(0, maxLength) + "..."
    : cleanedContent;

  return asChip ? (
    <Chip
      label={truncatedText}
      size="small"
      sx={{
        backgroundColor: "#f0f9ff",
        color: "#0369a1",
        "&:hover": { backgroundColor: "#e0f2fe" },
        "& .MuiChip-label": {
          fontSize: "1rem",
        },
        ...sx,
      }}
    />
  ) : (
    <Typography
      component="span"
      sx={{
        cursor: needsTruncation ? "pointer" : "default",
        color: needsTruncation ? "primary.main" : "inherit",
        "&:hover": { color: needsTruncation ? "primary.dark" : "inherit" },
        fontSize: "1.1rem",
        ...sx,
      }}
      onClick={
        needsTruncation ? () => openDialog("Full Content", cleanedContent) : undefined
      }
    >
      {truncatedText}
    </Typography>
  );
};

// SkillChips component (updated with parseStringToArray)
const SkillChips = ({ skills, openDialog, maxSkills = 3, chipFontSize }) => {
  const skillsArray = parseStringToArray(skills);

  const displaySkills = skillsArray
    .filter((skill) => skill && skill.trim())
    .slice(0, maxSkills)
    .map((skill) => cleanString(skill));

  const remainingCount = skillsArray.length - maxSkills;

  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
      {displaySkills.map((skill, index) => (
        <TruncatedContent
          key={index}
          content={skill}
          maxLength={15}
          openDialog={openDialog}
          asChip={true}
          sx={{ fontSize: chipFontSize }}
        />
      ))}
      {remainingCount > 0 && (
        <Chip
          label={`+${remainingCount}...`}
          size="small"
          onClick={() =>
            openDialog(
              "All Skills",
              skillsArray
                .map((s) => cleanString(s))
                .join(", ")
            )
          }
          sx={{
            backgroundColor: "#e5e7eb",
            color: "#4b5563",
            "&:hover": { backgroundColor: "#d1d5db" },
            cursor: "pointer",
          }}
        />
      )}
    </Box>
  );
};

const ViewToggleButton = styled(Button)(({ theme }) => ({
  borderRadius: "14px",
  textTransform: "none",
  fontWeight: 600,
  padding: "12px 24px",
  fontSize: "0.95rem",
  background: "linear-gradient(135deg, #0017E7 0%, rgba(0, 23, 231, 0.8) 100%)",
  color: "#ffffff",
  border: "none",
  boxShadow: "0 6px 20px rgba(0, 23, 231, 0.25)",
  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
  "&:hover": {
    background: "linear-gradient(135deg, rgba(0, 23, 231, 0.9) 0%, #0017E7 100%)",
    transform: "translateY(-2px)",
    boxShadow: "0 8px 25px rgba(0, 23, 231, 0.35)",
  }
}));

const DynamicFormTable2 = ({ data, viewType, setViewType, formStructure }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState("");
  const [dialogContent, setDialogContent] = useState("");
  const [scale, setScale] = useState(1);
  const [filteredData, setFilteredData] = useState(data);

  // Add scaling effect
  useEffect(() => {
    const handleResize = () => {
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;
      const contentHeight = 800; // Approximate height of your content
      const contentWidth = 1200; // Approximate width of your content
      
      // Calculate scale based on both height and width
      const heightScale = (windowHeight * 0.95) / contentHeight; // 95% of window height
      const widthScale = (windowWidth * 0.95) / contentWidth; // 95% of window width
      
      // Use the smaller scale to ensure content fits both dimensions
      let newScale = Math.min(heightScale, widthScale, 1);
      
      // Add minimum scale limit based on screen width
      if (windowWidth <= 1920) { // For 1080p and similar resolutions
        newScale = Math.max(newScale, 0.8); // Minimum 80% scale
      } else if (windowWidth <= 2560) { // For 1440p
        newScale = Math.max(newScale, 0.85); // Minimum 85% scale
      } else {
        newScale = Math.max(newScale, 0.9); // Minimum 90% scale for larger screens
      }
      
      setScale(newScale);
    };

    handleResize(); // Initial scale
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fields to exclude from display
  const excludedFields = [
    "gender",
    "city",
    "Cover letter",
    "image",
    "cv",
    "cover_letter",
    "submitted_at",
    "admin",
  ];

  // Get all unique fields and their labels
  const getFieldsAndLabels = () => {
    const fields = new Set();
    const labelMap = {};

    // Process each row to collect all unique fields
    data.forEach((row) => {
      if (row.form_data_with_labels) {
        Object.keys(row.form_data_with_labels)
          .filter((key) => !excludedFields.includes(key.toLowerCase()))
          .forEach((key) => {
            fields.add(key);
            labelMap[key] = key; // Use the label as is since it's already labeled from backend
          });
      }
    });

    return Array.from(fields).map((field) => ({
      name: field,
      label: labelMap[field] || field,
    }));
  };

  const openDialog = (title, content) => {
    setDialogTitle(title);
    setDialogContent(content);
    setDialogOpen(true);
  };

  const closeDialog = () => setDialogOpen(false);

  const handleSearch = (searchResults) => {
    setFilteredData(searchResults);
  };

  if (!data?.length) return null;

  const fields = getFieldsAndLabels();

  return (
    <Box sx={{ 
      transform: `scale(${scale})`,
      transformOrigin: 'top left',
      width: `${100 / scale}%`,
      height: `${100 / scale}%`,
      transition: 'transform 0.3s ease-in-out'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {viewType !== "card" ? (
          <ViewToggleButton
            onClick={setViewType}
            startIcon={<ViewModuleIcon />}
            sx={{ fontSize: "1.1rem" }}
          >
            Card View
          </ViewToggleButton>
        ) : (
          <Button
            variant="outlined"
            onClick={setViewType}
            startIcon={<ViewListIcon />}
            sx={{ height: 50, fontSize: "1.1rem" }}
          >
            List View
          </Button>
        )}
        <Box sx={{ flex: 1 }}>
          <ApplicationSearchBar
            data={data}
            onSearch={handleSearch}
            tableType="dynamicForms"
            SEARCH_API_ENDPOINT={`${apiUrl}candidates/api/dynamic-forms/search/`}
            AUTOCOMPLETE_API_ENDPOINT={`${apiUrl}candidates/api/dynamic-forms/autocomplete/`}
            location="location"
          />
        </Box>
      </Box>

      <Paper elevation={0} sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {fields.map((field) => (
                  <TableCell 
                    key={field.name} 
                    sx={{ 
                      fontWeight: 600, 
                      backgroundColor: "#f8fafc"
                    }}
                  >
                    {field.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.map((submission, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:nth-of-type(odd)": {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                    "&:hover": { backgroundColor: "#f8fafc" },
                  }}
                >
                  {fields.map((field) => (
                    <TableCell 
                      key={field.name}
                      sx={{ fontSize: "1.1rem" }}
                    >
                      {field.name.toLowerCase().includes("skill") ? (
                        <SkillChips
                          skills={submission.form_data_with_labels[field.name]}
                          openDialog={openDialog}
                          chipFontSize="1rem"
                        />
                      ) : (
                        <TruncatedContent
                          content={submission.form_data_with_labels[field.name]}
                          openDialog={openDialog}
                          sx={{ fontSize: "1.1rem" }}
                        />
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "1.3rem" }}>
          {dialogTitle}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: "1.1rem" }}>
            {dialogContent}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} sx={{ fontSize: "1.1rem" }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DynamicFormTable2;